import { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";

import CommissionGroupComponent from "./CommissionGroupComponent";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { BranchModel } from "../../../../../models/AdminPageModels";

interface AdminIccBranches {
    data: IccBranchesData;
    error: IError;
}
interface IccBranchesData {
    branches: BranchModel[];
}

interface GetSalesmen {
    data: GetSalesmenData;
    error: IError;
}
interface GetSalesmenData {
    salesmen: SalesmanModel[];
}

interface GetCommissionGroups {
    data: GetCommissionGroupsData;
    error: IError;
}
interface GetCommissionGroupsData {
    groups: CommissionGroupModel[];
}

interface SaveCommissionsRequest {
    clientId: number;
    groups: CommissionGroupModel[];
}
interface SaveCommissionsResponse {
    data: SaveCommissionsData;
    error: IError;
}
interface SaveCommissionsData {
    status: string;
}

export interface SalesmanModel {
    id: number;
    sales: string;
}

export interface CommissionGroupModel {
    groupNumber: number;
    groupName: string;
    clientId: number;
    salesId: number;
    periods: CommissionPediodModel[];
}

export interface CommissionPediodModel {
    id: number,
    percent: number;
    startDate: Dayjs;
    endDate: Dayjs | null;
}


function SetupCommission() {
    const { enqueueSnackbar } = useSnackbar();

    const [selectedBranch, setSelectedBranch] = useState<number>(0);

    const [groups, setGroups] = useState<CommissionGroupModel[]>([]);

    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [salesmen, setSalesmen] = useState<SalesmanModel[]>([]);

    useEffect(() => {
        GetBranches();
        GetSalesmen();
    }, []);

    const GetBranches = async () => {
        try {
            const response: AdminIccBranches = await api.get("api/Admin/GetICCBranches", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBranches(response.data.branches)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetSalesmen = async () => {
        try {
            const response: GetSalesmen = await api.get("api/Admin/GetSalesmen", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setSalesmen(response.data.salesmen)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    function SetSelectedBranch(event: any) {
        var value = event.target.value;
        setSelectedBranch(value);
        if (value !== 0) {
            GetCommissionGroups(value);
        }
        else {
            setGroups([])
        }
    }

    const GetCommissionGroups = async (clientId: number) => {
        setGroups([]);
        try {
            const response: GetCommissionGroups = await api.get(`api/Admin/GetCommissionGroups?id=${clientId}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setGroups(response.data.groups)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const RemoveGroup = useCallback((group: CommissionGroupModel) => {
        const newArray: CommissionGroupModel[] = [];
        for (var i = 0; i < groups.length; i++) {
            if (groups[i] === group)
                continue;
            newArray.push(groups[i]);
        }
        setGroups(newArray);
    }, [groups]);

    function AddGroup() {
        const newCommission: CommissionGroupModel = {
            groupNumber: 0,
            groupName: "",
            clientId: selectedBranch,
            salesId: 0,
            periods: []
        }
        setGroups(current => [...current, newCommission]);
    }

    const Save = async () => {
        var body: SaveCommissionsRequest = {
            clientId: selectedBranch,
            groups: groups
        };
        try {
            const response: SaveCommissionsResponse = await api.post("api/Admin/SaveCommission", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Saved!", { variant: "success" })
                GetCommissionGroups(selectedBranch);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }


    return (
        <Stack spacing={1}>
            {/* Branch */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '80px' }}>Branch: </p>
                <FormControl sx={{ width: "200px" }}>
                    <Select
                        value={selectedBranch}
                        onChange={SetSelectedBranch}
                        size="small"
                        defaultValue={0}>
                        <MenuItem
                            value={0}>
                            {"Select branch"}
                        </MenuItem>
                        {branches.map((type, index) =>
                            <MenuItem
                                key={index}
                                value={type.id}>
                                {type.branchName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    onClick={Save}
                    sx={{ width: "60px", left: "10px" }}>
                    Save
                </Button>
            </Stack>

            {groups.map((group, index) =>
                <CommissionGroupComponent
                    key={index}
                    group={group}
                    salesmen={salesmen}
                    removeGroup={RemoveGroup}
                />
            )}

            {selectedBranch !== 0 ?
                <Button
                    variant="outlined"
                    onClick={AddGroup}
                    sx={{ width: "150px" }}>
                    Add Group
                </Button>
                : null}
        </Stack>
    )
}

export default SetupCommission;